"use client";

import React, { useState, useRef, useEffect } from "react";

import FAQList from "@/components/global/ui/FAQList";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableTextBlock } from "sanity";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageCareerSchema } from "@/sanity/pages/PageCareerTemplate";
import julianBudach from "@/public/career/julianProfile.png";
import { CTAButton } from "@/shadcn/ui/button";

export default function FAQSection({ data, className }: { data: PageCareerSchema; className?: string }) {
  const faqName = data?.faqName;
  const faqRole = data?.faqRole;
  const items = data?.faqItems;
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  return (
    <section
      className={cn(
        "relative overflow-clip bg-white lg:border border-[#E5E5E5] rounded-[2.25rem] lg:rounded-[4rem] flex flex-col-reverse lg:flex-row gap-[4rem] lg:gap-[7rem] justify-center pt-[2rem] pb-[12rem] lg:pt-[12.38rem] lg:pb-[20rem] px-[2rem] lg:px-0",
        className,
      )}>
      <div className="flex flex-col items-center justify-between gap-[7rem] w-full lg:w-[min(49.875rem,100%)] lg:h-[72.375rem] p-[2.5rem] flex-shrink-0 bg-[#F0F4F2] rounded-[1.25rem] lg:rounded-[2rem]">
        <div className="flex flex-col w-full gap-[3rem]">
          <div className="flex flex-col justify-center items-center w-full lg:flex-row lg:justify-start gap-[2rem]">
            <Image
              src={julianBudach}
              alt="Julian Budach"
              className="object-cover w-[10rem] h-[10rem]"
            />
            <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
              <p className="font-instrument-sans text-[2.25rem] font-[600] text-[#103037] tracking-[-0.0675rem] leading-[140%] mb-0">
                {faqName}
              </p>
              <p className="font-geist text-[1.5rem] font-[500] uppercase text-[#103037]/70 leading-[150%] mb-0">
                {faqRole}
              </p>
            </div>
          </div>
          <div className="richtext richtext-center lg:richtext-start prose-p:text-[2rem] prose-p:text-[#1C1F22]/80 prose-p:tracking-[-0.01rem] prose-p:mb-0 prose-p:leading-[150%] prose-headings:text-[4rem] lg:prose-headings:text-[3.5rem] prose-headings:font-[600] prose-headings:text-[#103037] prose-headings:leading-[120%] prose-headings:tracking-[-0.105rem] prose-headings:mb-[1rem] lg:prose-headings:mb-0">
            <PortableText
              value={data?.faqText || []}
              components={richTextComponent}
            />
          </div>
        </div>

        <div className="flex flex-col justify-center w-full lg:justify-start">
          <div className="w-full mb-[1.5rem] lg:w-fit">
            <CTAButton data={data?.faqCta || {}} />
          </div>

          <div className="flex flex-col justify-center w-full text-center lg:justify-start lg:text-left text-[2rem] text-[#1C1F22] font-geist">
            <p className="mb-0 opacity-80">Du willst uns lieber eine Mail schreiben?</p>
            <p className="mb-0">info@iqcapital.io</p>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[min(89.625rem,100%)] lg:min-h-[72.375rem]">
        {items &&
          items.map((item, index) => (
            <div
              key={index}
              id={`faq-section-${index}`}
              ref={(el) => (sectionRefs.current[index] = el)}
              className={cn(
                "flex flex-col items-center",
                items && index < items.length - 1 && "border-b border-[#E5E5E5]",
              )}>
              <div className="px-0 sm:px-[4rem] w-[min(94.25rem,100%)] flex flex-col items-start gap-[2rem] lg:gap-[3rem]">
                <h2 className="mb-0 text-[#103037] text-[5rem] lg:text-[7rem] leading-[112%]">{item?.title}</h2>
                <FAQList faq={item.faqList} />
              </div>
            </div>
          ))}
      </div>
    </section>
  );
}
